import { supabase } from './supabaseClient';
import { simboloMoneda, getNegocioId } from './negocioActual';
import { getMarcaId } from './marcaActual';

const LOLITAS = 'f88a3687-244d-469c-ad65-00f65bbc14c0';

// Texto por defecto de la publicación (debe coincidir con el del servidor,
// api/publicar-redes.js). Se muestra editable antes de publicar.
export function construirCaption(p) {
  const venta = Number(p.precio_venta || 0);
  const oferta = (p.precio_oferta != null && Number(p.precio_oferta) > 0 && Number(p.precio_oferta) < venta) ? Number(p.precio_oferta) : null;
  const partes = [];
  // Orden: precio + talla juntos, nombre, marca, código, descripción,
  // llamado a la acción y hashtags.
  const s = simboloMoneda();
  const precio = oferta != null ? `💵 ${s} ${oferta} 🔥 (antes ${s} ${venta})` : (venta ? `💵 ${s} ${venta}` : '');
  const precioTalla = [precio, p.talla && `📏 Talla ${p.talla}`].filter(Boolean).join('  ·  ');
  if (precioTalla) partes.push(precioTalla);
  partes.push(`✨ ${p.nombre} ✨`);
  if (p.marca) partes.push(`🏷️ Marca: ${p.marca}`);
  if (p.color) partes.push(`🎨 Color: ${p.color}`);
  if (p.codigo) partes.push(`🔖 Código: ${p.codigo}`);
  if (p.descripcion) { partes.push(''); partes.push(p.descripcion); }
  partes.push('');
  partes.push('📩 Escríbenos para apartar el tuyo 💕');
  // Hashtags propios de Lolita's Store; los demás negocios escriben los suyos.
  if (getNegocioId() === LOLITAS) {
    partes.push('');
    partes.push('#LolitasStore #Olanchito #Yoro #Honduras #ModaFemenina #Lenceria');
  }
  return partes.join('\n');
}

// Llama al endpoint de servidor que publica en Meta. Manda el token de sesión
// para que el servidor verifique que quien publica es la dueña del negocio.
export function publicarEnRedes({ productoId, redes, caption }) {
  return llamarPublicador({ productoId, redes, caption });
}

// Carrusel armado en Contenido: láminas ya subidas a ai-generaciones/<negocio>/carruseles/.
// origen = { ideaId, materialesId, titulo } para que quede ligado en el calendario.
export function publicarCarrusel({ negocioId, redes, caption, captionFacebook, imagenes, tipo, origen = {} }) {
  return llamarPublicador({ negocioId, redes, caption, captionFacebook, imagenes, ...(tipo ? { tipo } : {}), ...origen });
}

// Reel con el video de la dueña: iniciar (Meta empieza a procesarlo) y luego
// preguntar el estado hasta que salga publicado. Con publicacionId el servidor
// usa lo guardado (y lo termina solo aunque se cierre la ventana).
export function iniciarReel({ negocioId, redes, caption, captionFacebook, video, origen = {} }) {
  return llamarPublicador({ accion: 'reel_iniciar', negocioId, redes, caption, captionFacebook, video, ...origen });
}
export function estadoReel({ negocioId, publicacionId, contenedorIg, videoFb }) {
  return llamarPublicador({ accion: 'reel_estado', negocioId, publicacionId, contenedorIg, videoFb });
}

// Pide a Meta (solo lectura) los resultados de una publicación ya hecha.
export function actualizarMetricas({ negocioId, publicacionId }) {
  return llamarPublicador({ accion: 'metricas', negocioId, publicacionId });
}

// Programar contenido YA APROBADO (láminas o video ya subidos) para una fecha y hora.
// tipo: 'carrusel' | 'historias' | 'reel'. Devuelve { ok, publicacion: { id, programada_para } }.
export async function programarPublicacion({ tipo, negocioId, redes, caption, captionFacebook, imagenes, video, programadaPara, origen = {} }) {
  const r = await llamarPublicador({
    accion: 'programar', tipo, negocioId, redes, caption, captionFacebook, imagenes, video,
    programadaPara: new Date(programadaPara).toISOString(), ...origen,
  });
  if (!r?.ok) { const e = new Error(r?.mensaje || 'No se pudo programar.'); e.code = r?.error || 'ERROR'; throw e; }
  return r;
}

// "Subir publicación lista" (sql/139): la planeada pasa a programada (sale sola)
// si su marca tiene las redes conectadas. Sin fecha, usa la que ya tenía.
export async function programarLista({ publicacionId, programadaPara = null }) {
  const r = await llamarPublicador({
    accion: 'programar', negocioId: getNegocioId(), publicacionId,
    ...(programadaPara ? { programadaPara: new Date(programadaPara).toISOString() } : {}),
  });
  if (!r?.ok) { const e = new Error(r?.mensaje || 'No se pudo programar.'); e.code = r?.error || 'ERROR'; throw e; }
  return r;
}

// "Conectar mi Facebook e Instagram" (sql/109). Solo la dueña. Con "Mis marcas"
// (sql/140) cada marca conecta las suyas: va la marca elegida (llamarPublicador).
export const estadoRedes = ({ negocioId }) => llamarPublicador({ accion: 'meta_estado', negocioId });
export const elegirPaginaRedes = ({ negocioId, pageId }) => llamarPublicador({ accion: 'meta_elegir', negocioId, pageId });
export const desconectarRedes = ({ negocioId }) => llamarPublicador({ accion: 'meta_desconectar', negocioId });
// Lleva al inicio de sesión oficial de Facebook; al terminar vuelve a Kontrol con ?meta=…
export async function conectarRedes({ negocioId }) {
  const r = await llamarPublicador({ accion: 'meta_inicio', negocioId });
  if (!r?.url) throw new Error(r?.mensaje || 'No se pudo abrir Facebook.');
  window.location.assign(r.url);
}

async function llamarPublicador(cuerpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { const e = new Error('Inicia sesión para publicar.'); e.code = 'NO_AUTENTICADO'; throw e; }

  let resp;
  try {
    resp = await fetch('/api/publicar-redes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // "Mis marcas": lo que se publique o programe queda en la marca elegida.
      body: JSON.stringify(getMarcaId() && !cuerpo.marcaId ? { ...cuerpo, marcaId: getMarcaId() } : cuerpo),
    });
  } catch (e) {
    const err = new Error('No se pudo conectar con el servidor de publicación.');
    err.code = 'SIN_CONEXION';
    throw err;
  }

  const data = await resp.json().catch(() => ({}));
  // Si viene el detalle por red (200 = alguna ok, 502 = todas fallaron),
  // lo devolvemos para MOSTRAR el error exacto de Meta en cada red.
  if (data && data.resultados) return data;
  if (!resp.ok) {
    const err = new Error(data?.mensaje || 'No se pudo publicar.');
    err.code = data?.error || 'ERROR';
    throw err;
  }
  return data; // { ok, resultados: { instagram?, facebook? } }
}
